import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Button from '../../../components/common/Button'
import styled from '@emotion/styled'
import ListPage from '../../../components/templates/ListPage'
import { Authorize } from '../../../components/Authorize'
import { ROLE } from '../../../services/user/user-typed'
import Text from '../../../components/common/Text'
import { Chip, makeStyles, Modal } from '@material-ui/core'
import { arrayMove } from '@dnd-kit/sortable'
import { DragEndEvent } from '@dnd-kit/core'
import * as paths from '../../../constants/path'
import { useRouter } from '../../../../utils/helper'
import { ColumnsProps } from '../../../components/common/TableDnd'
import { useToastForReactQuery, useVisible } from '../../../../utils/custom-hooks'
import ConfirmCard from '../../../components/common/Card'
import { useGetTags, useUpdateTags } from '../../../services/tag/tag-query'
import { isEmpty } from 'lodash'
import { IGetTagDetailResponse } from '../../../services/tag/tag-type'
import { useTranslation } from 'react-i18next'

const Layout = styled.div`
  display: flex;
  flex: 1;
  flex-flow: row;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  padding-right: 16px;
`

const ButtonStyled = styled(Button)`
  padding: 7px 13px;
`

const useStyles = makeStyles(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

const TagList = () => {
  const [editRank, setEdit] = useState(false)
  const { push } = useRouter()
  const classes = useStyles()
  const onConfirmModalVisible = useVisible()
  const { data: tags, isLoading } = useGetTags()
  const { mutate: updateTags } = useUpdateTags()
  const toast = useToastForReactQuery()
  const { t } = useTranslation()
  const items = tags?.items ?? []
  const [rows, setRows] = useState<IGetTagDetailResponse[]>(items)
  console.debug(tags, 'check')

  useEffect(() => {
    if (!isEmpty(tags?.items) && !isLoading) {
      setRows(tags?.items ?? [])
    }
  }, [isLoading, items, tags])

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      const oldIndex = rows.findIndex(item => item.id === active.id)
      const newIndex = rows.findIndex(item => item.id === over?.id)
      const newRows = arrayMove(rows, oldIndex, newIndex).map((item, idx) => ({
        ...item,
        rank: idx + 1, // 🆕 assign rank
      }))
      setRows(newRows)
      console.debug('Updated Rows with Rank:', newRows)
    }
  }

  const onClickView = useCallback(
    (id: string) => {
      push(
        paths.tagDetail({
          routeParam: {
            id: id,
          },
        }),
      )
    },
    [push],
  )

  const onClickDelete = useCallback(
    (id: string) => {
      const filtered = rows.filter(row => row.id !== id)
      const reordered = filtered.map((item, idx) => ({
        ...item,
        rank: idx + 1, // re-rank after deletion
      }))
      setRows(reordered)
    },
    [rows],
  )

  const columns: ColumnsProps = useMemo(() => {
    return [
      {
        title: 'Rank',
        dataIndex: 'rank',
      },
      {
        title: 'Name',
        dataIndex: 'name',
        render: (text: string, record: { route: string }) => {
          return <Text>{text}</Text>
        },
      },
      {
        title: 'Tag key',
        dataIndex: 'tagKey',
        render: (text: string) => {
          return text ? <Text>{text}</Text> : <Text>{`-`}</Text>
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        render: (text: string, record: { status: boolean }) => {
          const { status } = record
          return status ? (
            <Chip label="Active" color="primary" style={{ color: 'white' }} />
          ) : (
            <Chip label="Inactive" variant="outlined" color="default" />
          )
        },
      },
      {
        title: 'Image',
        dataIndex: 'activeImage',
        render: (text: string) => {
          return <img alt={'active'} src={text} width={40} height={40} />
        },
      },
      {
        title: '',
        dataIndex: 'action',
        render: (text: string, record: { id: string }) => {
          return !editRank ? (
            <Authorize role={ROLE.MANAGER}>
              <Button onClick={() => onClickView(record.id)}>View</Button>
            </Authorize>
          ) : (
            <Authorize role={ROLE.ADMIN}>
              <Button variant={'danger'} onClick={() => onClickDelete(record.id)}>
                Delete
              </Button>
            </Authorize>
          )
        },
      },
    ]
  }, [editRank, onClickDelete, onClickView])

  // const setQueryParam = useCallback(
  //   params => {
  //     push(paths.tag({ queryParam: { ...query, ...params } }))
  //   },
  //   [push, query],
  // )

  // const onSearch = useCallback(
  //   text => {
  //     setQueryParam({ q: text, page: 1 })
  //   },
  //   [setQueryParam],
  // )

  const onClickCreate = useCallback(() => {
    push(paths.createTag())
  }, [push])

  const handleOpenConfirmModal = useCallback(() => {
    onConfirmModalVisible.open()
  }, [onConfirmModalVisible])

  const handleCloseConfirmModal = useCallback(() => {
    onConfirmModalVisible.close()
  }, [onConfirmModalVisible])

  const onEditRank = useCallback(() => {
    setRows(tags?.items ?? [])
    setEdit(true)
  }, [tags])

  const handleSubmit = useCallback(() => {
    console.debug(rows)
    updateTags(
      {
        tags: rows,
      },
      {
        onSuccess: () => {
          toast.open('Success', {
            type: 'success',
            autoClose: 3 * 1000,
          })
          handleCloseConfirmModal()
          setEdit(false)
        },
        onError: (error: any) => {
          toast.open(t(`${error}`), {
            type: 'error',
            autoClose: 3 * 1000,
          })
          handleCloseConfirmModal()
        },
      },
    )
  }, [handleCloseConfirmModal, rows, t, toast, updateTags])

  const onCancelEdit = useCallback(() => {
    setEdit(false)
  }, [])

  return (
    <>
      <ListPage
        topbar={
          <Layout>
            <Authorize role={ROLE.ADMIN}>
              {editRank ? (
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                  <ButtonStyled variant="warning" onClick={handleOpenConfirmModal}>
                    Submit
                  </ButtonStyled>

                  <ButtonStyled
                    variant="warning"
                    style={{
                      backgroundColor: '#FF0000',
                      borderColor: '#FF0000',
                    }}
                    onClick={onCancelEdit}
                  >
                    Cancel
                  </ButtonStyled>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                  {!isEmpty(tags?.items) && (
                    <ButtonStyled variant="warning" onClick={onEditRank}>
                      Edit Rank
                    </ButtonStyled>
                  )}

                  <ButtonStyled variant="warning" onClick={onClickCreate}>
                    Create
                  </ButtonStyled>
                </div>
              )}
            </Authorize>
          </Layout>
        }
        tableProps={{
          columns,
          data: editRank ? rows : items,
        }}
        onDragEnd={editRank ? onDragEnd : undefined}
        showPaginate={false}
        // searchInputProps={{ onSearch, value: query.q, placeholder: 'Search name' }}
      />
      <Modal
        className={classes.modal}
        open={onConfirmModalVisible.visible}
        onClose={onConfirmModalVisible.close}
      >
        <ConfirmCard
          style={{
            display: 'flex',
            width: '300px',
            padding: '30px',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Text>Confirm</Text>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignSelf: 'center',
              paddingTop: '10px',
            }}
          >
            <Button
              style={{
                alignSelf: 'center',
                marginRight: '20px',
              }}
              onClick={handleSubmit}
            >
              Yes
            </Button>
            <Button
              style={{
                alignSelf: 'center',
                backgroundColor: '#FF0000',
                borderColor: '#FF0000',
              }}
              onClick={onConfirmModalVisible.close}
            >
              No
            </Button>
          </div>
        </ConfirmCard>
      </Modal>
    </>
  )
}

export default TagList
